import React, { useEffect, useState } from "react";
import axios from "axios";
import ReusableDataTable from "../../hooks/ReusableDataTable";
import { Button } from "flowbite-react";
import { useSidebar } from "../../context/SidebarOpenContext";

// PDF
import jsPDF from "jspdf";
import "jspdf-autotable";

function AllStock() {

    const { isSidebarOpen } = useSidebar();

    const [allStockData, setAllStockData] = useState([]);
    const [stockFilter, setStockFilter] = useState("all");

    // =========================
    // FORMAT CURRENCY
    // =========================
    const formatCurrency = (num, includeSymbol = true) => {

        if (includeSymbol) {
            return new Intl.NumberFormat("en-IN", {
                style: "currency",
                currency: "INR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            }).format(num || 0);
        }

        return new Intl.NumberFormat("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(num || 0);
    };

    // =========================
    // DATE FORMAT
    // =========================
    const formatDate = (dateStr) => {

        const date = new Date(dateStr);

        if (isNaN(date.getTime())) return dateStr;

        const d = String(date.getDate()).padStart(2, "0");
        const m = String(date.getMonth() + 1).padStart(2, "0");
        const y = date.getFullYear();

        return `${d}-${m}-${y}`;
    };

    const today = formatDate(new Date());

    // =========================
    // FETCH ALL STOCK
    // =========================
    const fetchAllStocks = async () => {

        try {

            const [fabricRes, trimsRes] = await Promise.all([
                axios.get("master/fabrics"),
                axios.get("master/raw_material_stocks"),
            ]);

            // FABRICS
            const fabrics = fabricRes.data.map((item) => ({
                type: "Fabric",
                name: item.fabric_content,
                category: item.finish,
                extra: `Roll No: ${item.roll_no}`,
                stock: Number(item.count || 0),
                rate: Number(item.single_qty_rate || 0),
                total: Number(item.total_price || 0),
                unit: "Roll",
            }));

            // TRIMS
            const trims = trimsRes.data.map((item) => ({
                type: "Trim",
                name: item.raw_material_name,
                category: item.trims_type,
                extra: item.size || "-",
                stock: Number(item.stock_count || 0),
                rate: Number(item.single_qty_rate || 0),
                total: Number(item.stock_price || 0),
                unit: item.unit || "-",
            }));

            setAllStockData([...fabrics, ...trims]);

        } catch (error) {

            console.error("Error fetching all stock:", error);
        }
    };

    useEffect(() => {

        fetchAllStocks();

    }, []);

    // FILTERED DATA
    // =========================
    const filteredData = allStockData.filter((row) => {

        // ALL
        if (stockFilter === "all") {
            return true;
        }

        // FABRICS
        if (stockFilter === "fabric") {
            return row.type === "Fabric";
        }

        // ALL TRIMS
        if (stockFilter === "trim") {
            return row.type === "Trim";
        }

        // TRIMS TYPES
        if (
            stockFilter === "general" ||
            stockFilter === "packing" ||
            stockFilter === "sewing" ||
            stockFilter === "thread"
        ) {
            return (
                row.type === "Trim" &&
                row.category?.toLowerCase() === stockFilter
            );
        }

        return true;
    });
    // =========================
    // TOTALS
    // =========================
    const totalStock = filteredData.reduce(
        (sum, row) => sum + Number(row.stock || 0),
        0
    );

    const totalPrice = filteredData.reduce(
        (sum, row) => sum + Number(row.total || 0),
        0
    );

    // =========================
    // TABLE DATA
    // =========================
    // =========================
    

    // =========================
    // TABLE DATA
    // =========================
    const data = filteredData.map((row, index) => ({
        sno: index + 1,
        ...row,
    }));

    const columns = [
        {
            name: "S.No",
            selector: (row) => row.sno,
            width: "80px",
        },
        {
            name: "Type",
            selector: (row) => row.type,
            sortable: true,
        },
        {
            name: "Name",
            selector: (row) => row.name,
            sortable: true,
        },
        {
            name: "Category",
            selector: (row) => row.category,
            sortable: true,
        },
        {
            name: "Extra Details",
            selector: (row) => row.extra,
            sortable: true,
        },
        {
            name: "Stock",
            selector: (row) => row.stock,
            sortable: true,
        },
        {
            name: "Rate (₹)",
            selector: (row) => formatCurrency(row.rate),
            sortable: true,
        },
        {
            name: "Total Price (₹)",
            selector: (row) => formatCurrency(row.total),
            sortable: true,
        },
        {
            name: "Unit",
            selector: (row) => row.unit,
            sortable: true,
        },
    ];

    // =========================
    // EXPORT DATA
    // =========================
    const exportHeaders = [
        "S.No",
        "Type",
        "Name",
        "Category",
        "Extra Details",
        "Stock",
        "Rate",
        "Total Price",
        "Unit",
    ];

    const getExportData = () => {

        return data.map((row) => [
            row.sno,
            row.type,
            row.name,
            row.category,
            row.extra,
            row.stock,
            formatCurrency(row.rate, false),
            formatCurrency(row.total, false),
            row.unit,
        ]);
    };

    // =========================
    // PRINT
    // =========================
    const handlePrint = () => {

        const rows = data.map((row) => `
            <tr>
                <td>${row.sno}</td>
                <td>${row.type}</td>
                <td>${row.name}</td>
                <td>${row.category}</td>
                <td>${row.extra}</td>
                <td>${row.stock}</td>
                <td>${formatCurrency(row.rate, false)}</td>
                <td>${formatCurrency(row.total, false)}</td>
                <td>${row.unit}</td>
            </tr>
        `).join("");

        const win = window.open("", "_blank");

        win.document.write(`
            <html>
                <head>

                    <title>All Stock Report</title>

                    <style>

                        @page {
                            size: landscape;
                            margin: 10mm;
                        }

                        body {
                            font-family: Arial, sans-serif;
                            padding: 10px;
                            color: #333;
                        }

                        h2 {
                            text-align: center;
                        }

                        table {
                            width: 100%;
                            border-collapse: collapse;
                            margin-top: 20px;
                        }

                        th {
                            background: #222;
                            color: white;
                            padding: 10px;
                            border: 1px solid #ccc;
                            font-size: 12px;
                        }

                        td {
                            border: 1px solid #ccc;
                            padding: 8px;
                            font-size: 11px;
                        }

                        .total-row {
                            background: #f3f3f3;
                            font-weight: bold;
                        }

                    </style>

                </head>

                <body>

                    <h2>ALL STOCK REPORT</h2>

                    <p style="text-align:center">
                        Generated On : ${today}
                    </p>

                    <table>

                        <thead>

                            <tr>
                                ${exportHeaders.map(h => `<th>${h}</th>`).join("")}
                            </tr>

                        </thead>

                        <tbody>

                            ${rows}

                            <tr class="total-row">

                                <td colspan="5" style="text-align:right">
                                    OVERALL TOTAL :
                                </td>

                                <td>${totalStock}</td>

                                <td>-</td>

                                <td>${formatCurrency(totalPrice, false)}</td>

                                <td></td>

                            </tr>

                        </tbody>

                    </table>

                </body>

            </html>
        `);

        win.document.close();

        setTimeout(() => {

            win.focus();
            win.print();
            win.close();

        }, 300);
    };

    // =========================
    // PDF
    // =========================
    const downloadPDF = () => {

        const doc = new jsPDF("l", "pt", "a4");

        doc.setFontSize(16);

        doc.text("ALL STOCK REPORT", 40, 45);

        doc.setFontSize(10);

        doc.text(`Date : ${today}`, 720, 45);

        const tableRows = getExportData();

        tableRows.push([
            {
                content: "OVERALL TOTAL",
                colSpan: 5,
                styles: {
                    halign: "right",
                    fontStyle: "bold",
                },
            },
            {
                content: totalStock.toString(),
                styles: {
                    fontStyle: "bold",
                },
            },
            {
                content: "-",
                styles: {
                    fontStyle: "bold",
                },
            },
            {
                content: formatCurrency(totalPrice, false),
                styles: {
                    fontStyle: "bold",
                },
            },
            {
                content: "",
            },
        ]);

        doc.autoTable({
            head: [exportHeaders],
            body: tableRows,
            startY: 65,
            theme: "grid",
            styles: {
                fontSize: 8,
            },
            headStyles: {
                fillColor: [40, 40, 40],
            },
        });

        doc.save(`All_Stock_Report_${today}.pdf`);
    };

    // =========================
    // CSV
    // =========================
    const downloadCSV = () => {

        const rows = getExportData().map((row) =>
            row.map((cell) => `"${cell}"`)
        );

        rows.push([
            "",
            "",
            "",
            "",
            `"OVERALL TOTAL"`,
            `"${totalStock}"`,
            `"-"`,
            `"${formatCurrency(totalPrice, false)}"`,
            "",
        ]);

        const csvContent =
            "\uFEFF" +
            [exportHeaders.map((h) => `"${h}"`), ...rows]
                .map((e) => e.join(","))
                .join("\n");

        const blob = new Blob(
            [csvContent],
            { type: "text/csv;charset=utf-8;" }
        );

        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");

        link.href = url;

        link.setAttribute(
            "download",
            `All_Stock_Report_${today}.csv`
        );

        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
    };

    return (

        <div className={`${isSidebarOpen
            ? "p-4 sm:ml-64 bg-content-bg min-h-screen"
            : "p-4 bg-content-bg min-h-screen"
            }`}>

            <div className="p-4 rounded-lg mt-14">

                <div className="p-4 rounded-lg shadow bg-white">

                    {/* TOP BAR */}
                    <div className="flex flex-wrap justify-between items-center mb-4 gap-4">

                        <h2 className="text-2xl font-bold">

                        </h2>

                        <div className="flex flex-wrap gap-2 items-center">

                            {/* FILTER */}
                            <select
                                value={stockFilter}
                                onChange={(e) => setStockFilter(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                            >
                                <option value="all">All Stock</option>
                                <option value="fabric">All Fabrics</option>
                                <option value="trim">All Trims</option>

                                <option disabled>──────────</option>

                                <option value="general">General Trims</option>
                                <option value="packing">Packing Trims</option>
                                <option value="sewing">Sewing Trims</option>
                                <option value="thread">Thread Trims</option>
                            </select>

                            <button
                                onClick={handlePrint}
                                className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-black text-white rounded-lg transition-all shadow-md font-medium text-sm"
                            >
                                🖨️ Print
                            </button>

                            <button
                                onClick={downloadPDF}
                                className="flex items-center gap-2 px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg transition-all shadow-md font-medium text-sm"
                            >
                                📄 PDF
                            </button>

                            <button
                                onClick={downloadCSV}
                                className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all shadow-md font-medium text-sm"
                            >
                                📥 CSV
                            </button>

                        </div>

                    </div>

                    {/* TABLE */}
                    <ReusableDataTable
                        title="All Stocks List"
                        columns={columns}
                        data={data}
                    />

                </div>

            </div>

        </div>
    );
}

export default AllStock;